import React from "react";
import {
  EuiText,
  EuiMarkdownFormat,
  EuiAccordion,
  EuiPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiTitle,
  EuiTextColor,
  EuiCallOut,
  EuiTabbedContentTab,
  EuiSpacer,
} from "@elastic/eui";
import { useAnketa } from "../context_provider";
import { Section, SectionType } from "anketa-core";
import { SectionCardProps } from "src/questionnaires/types";
import { TabTitle } from "../tab_title";

export const SectionCard = ({ section }: SectionCardProps): JSX.Element => {
  const ctx = useAnketa();

  if (!section.visible) {
    return <div></div>;
  }
  let title;
  if (section.sectionType === SectionType.Volume) {
    title = <h2>{ctx.i18nContext.render(section.title)}</h2>;
  } else if (section.sectionType === SectionType.Chapter) {
    title = <h3>{ctx.i18nContext.render(section.title)}</h3>;
  } else {
    let parent = section.parent;
    let lvl = 1;
    while (parent !== null) {
      if (
        parent instanceof Section &&
        parent.sectionType === SectionType.SubChapter
      ) {
        lvl++;
        if (lvl > 2) {
          break;
        }
      }
      parent = parent.parent;
    }
    // Keep heading levels monotonic under Volume(h2) → Chapter(h3): the first
    // sub-chapter is h4, the next h5, and anything deeper h6 - no skipped levels.
    if (lvl > 2) {
      title = <h6>{ctx.i18nContext.render(section.title)}</h6>;
    } else if (lvl === 1) {
      title = <h4>{ctx.i18nContext.render(section.title)}</h4>;
    } else {
      title = <h5>{ctx.i18nContext.render(section.title)}</h5>;
    }
  }

  const buttonContent = () => {
    return (
      <div id={`section-button-${section.path}`} tabIndex={-1}>
        <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
          <EuiFlexItem>
            <a href={"#tree-" + section.path} id={section.path}>
              <EuiTitle size="xs">
                <h3>{ctx.i18nContext.render(section.title)}</h3>
              </EuiTitle>
            </a>
          </EuiFlexItem>
        </EuiFlexGroup>

        {section.help && (
          <EuiText size="s">
            <p>
              <EuiTextColor color="subdued">
                {ctx.i18nContext.render(section.help)}
              </EuiTextColor>
            </p>
          </EuiText>
        )}
      </div>
    );
  };

  return (
    <EuiAccordion
      id={section.id}
      buttonContent={buttonContent()}
      initialIsOpen
      className="ouiAccordionForm"
      buttonClassName="ouiAccordionForm__button"
      paddingSize="l"
    >
      <EuiText>
        {section.description && (
          <EuiCallOut>
            <EuiMarkdownFormat>
              {ctx.i18nContext.render(section.description)}
            </EuiMarkdownFormat>
          </EuiCallOut>
        )}
      </EuiText>
      {ctx.childrenForItem(section)}
    </EuiAccordion>
  );
};

const GetSectionTabContent = ({
  section,
  parentId,
}: {
  section: Section;
  parentId: string;
}): JSX.Element => {
  const ctx = useAnketa();

  return (
    <EuiPanel
      hasBorder={false}
      hasShadow={false}
      color="transparent"
      paddingSize="l"
      key={`tab-content-${parentId}-content`}
    >
      {section.description && (
        <>
          <EuiCallOut size="s">
            <EuiMarkdownFormat>
              {ctx.i18nContext.render(section.description)}
            </EuiMarkdownFormat>
          </EuiCallOut>
          <EuiSpacer size="m" />
        </>
      )}
      {ctx.childrenForItem(section)}
    </EuiPanel>
  );
};
export const getSectionTab = ({
  section,
  parentId,
}: {
  section: Section;
  parentId: string;
}): EuiTabbedContentTab => {
  return {
    id: `tab-${parentId}-${section.id}`,
    name: <TabTitle item={section} />,
    content: (
      <GetSectionTabContent
        section={section}
        parentId={`${parentId}-${section.id}`}
      />
    ),
  };
};
